import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  FileVideo,
  ImagePlus,
  Languages,
  MousePointerClick,
  Palette,
  RotateCcw,
  Upload,
  UserRound,
  Wand2,
  X,
} from 'lucide-react';
import './HookChangerPage.css';
import UniversalLayout from './components/UniversalLayout';
import { useAuthContext } from './components/AuthProvider';
import AuthModal from './components/AuthModal';
import AgentAutoReloadModal from './components/AgentAutoReloadModal';
import {
  generateTemplateVariants,
  getAgentEligibility,
  initializeProject,
  startAgentCompose,
} from './lib/api';
import {
  fetchCharactersIfStale,
  fetchLibraryCharactersIfStale,
  useCharactersData,
  useLibraryCharactersData,
} from './stores';

const MAX_VARIANTS = 10;
const MIN_VARIANTS = 1;
const MAX_STYLE_REFERENCE_IMAGES = 6;

const TEMPLATE_CONFIGS = {
  'change-avatar': {
    title: 'Change Avatar',
    titlePrefix: 'Change',
    titleEm: 'Avatar',
    icon: UserRound,
    variantNoun: 'avatar',
    variantPlural: 'avatars',
    countLabel: 'Selected character',
    generateLabel: 'Apply Avatar',
    generatingLabel: 'Starting generation',
    resultsTitle: 'Generated avatars',
    subtitle: 'Upload an existing video, choose a character, then rebuild it with that character as the presenter.',
    sourceDescription: 'The agent uses this as the source structure to rebuild.',
    whatThisDoes: 'Change Avatar sends the source video and selected library character directly to the general video agent so it can replace the presenter while keeping the original message.',
    steps: ['Upload source', 'Choose character', 'Generate video'],
    sourceTag: 'change_avatar_template',
    directCompose: true,
    requiresCharacterTarget: true,
    characterTargetLabel: 'Target character',
    characterTargetDescription: 'Choose from your characters or the Latted character library.',
    buildPrompt: (variant = {}, context = {}) => {
      const character = context.selectedCharacter || {};
      const characterDetails = [
        character.id ? `Character ID: ${character.id}` : null,
        character.name ? `Name: ${character.name}` : null,
        character.physicalAppearance ? `Physical appearance: ${character.physicalAppearance}` : null,
        character.personality ? `Personality: ${character.personality}` : null,
        character.keywords?.length ? `Tags: ${character.keywords.join(', ')}` : null,
      ].filter(Boolean).join('\n') || 'Use the selected character details as the target avatar.';

      return `Use the attached source video as the reference and rebuild it as a new editable Latted project with a changed avatar/persona. Use the change-avatar-video skill and follow it exactly, including recreating the full source video shot by shot at its original duration.

Template: Change Avatar

Selected character:
${characterDetails}

Character reference:
No character image URL is attached from the character API. Use the selected character ID and details as the target avatar description.`;
    },
  },
  'change-style': {
    title: 'Change Style',
    titlePrefix: 'Change',
    titleEm: 'Style',
    icon: Palette,
    variantNoun: 'style',
    variantPlural: 'styles',
    countLabel: 'Number of new styles',
    generateLabel: 'Apply Style',
    generatingLabel: 'Starting generation',
    resultsTitle: 'Generated styles',
    subtitle: 'Upload an existing video, describe or reference a target style, then rebuild versions with a fresh look and the same message.',
    sourceDescription: 'The agent uses this as the source structure to restyle.',
    whatThisDoes: 'Change Style sends the source video and your target style directly to the general video agent so it can rebuild the project with the same message and a new look.',
    steps: ['Upload source', 'Set target style', 'Generate video'],
    sourceTag: 'change_style_template',
    directCompose: true,
    requiresStyleTarget: true,
    styleTargetLabel: 'Target style',
    styleTargetDescription: 'Describe the new style or attach reference images.',
    styleTargetPlaceholder: 'Example: polished SaaS launch film with crisp UI closeups, soft studio lighting, restrained motion graphics, and clean white/graphite typography.',
    buildPrompt: (variant = {}, context = {}) => {
      const styleInstruction = (context.styleInstruction || '').trim();
      const referenceCount = context.styleReferenceCount || 0;
      const targetStyle = (variant.variantInstruction || styleInstruction || '').trim();
      const referenceNote = referenceCount > 0
        ? `\nStyle reference images: ${referenceCount} image${referenceCount === 1 ? '' : 's'} attached after the source video. Use these images as the target visual style guide, not as new subject/product content.`
        : '';

      return `Use the attached source video as the reference and rebuild it as a new editable Latted project with a changed visual style. Use the change-style-video skill and follow it exactly, including recreating the full source video shot by shot at its original duration.

Template: Change Style

Target style:
${targetStyle || 'No written style brief was provided. Use the attached style reference images as the target style guide.'}${referenceNote}`;
    },
  },
  'change-cta': {
    title: 'Change CTA',
    titlePrefix: 'Change',
    titleEm: 'CTA',
    icon: MousePointerClick,
    variantNoun: 'CTA',
    variantPlural: 'CTAs',
    countLabel: 'Number of new CTAs',
    generateLabel: 'Generate CTAs',
    generatingLabel: 'Generating CTAs',
    resultsTitle: 'Generated CTAs',
    subtitle: 'Upload an existing video, generate new call-to-action endings, then rebuild variants that preserve the original hook and body.',
    sourceDescription: 'The LLM reads this once to propose CTA replacements.',
    whatThisDoes: 'Change CTA keeps the hook and body intact, creates alternate conversion asks, and sends each selected CTA to the general video agent as a rebuild instruction.',
    steps: ['Upload source', 'Generate CTAs', 'Rebuild variants'],
    sourceTag: 'change_cta_template',
    buildPrompt: (variant) => `Use the attached source video as the reference and rebuild it as a new editable Latted project with a changed call to action. Use the change-cta-video skill and follow it exactly, replacing only the final CTA segment and keeping the rest of the source video untouched.

Template: Change CTA

New CTA direction:
${variant.variantInstruction}

Creative strategy:
${variant.strategy || 'Test a different conversion ask while preserving the original video.'}`,
  },
  'localize-ad': {
    title: 'Localize Ad',
    titlePrefix: 'Localize',
    titleEm: 'Ad',
    icon: Languages,
    variantNoun: 'localized ad',
    variantPlural: 'localized ads',
    countLabel: 'Target locale',
    generateLabel: 'Localize Ad',
    generatingLabel: 'Starting localization',
    resultsTitle: 'Localized ad',
    subtitle: 'Adapt an existing social ad for a new language and market while preserving its product, claims, presenter, and conversion intent.',
    sourceDescription: 'The agent analyzes the full ad, then uses Seedance 2.5 for localized visual edits.',
    whatThisDoes: 'Seedance 2.5 attempts the complete localization—including visible text, market-specific details, and spoken dialogue—in verified source segments. Visual overlays are never used. A separate voiceover is used only if the direct audio edit fails verification.',
    steps: ['Upload source ad', 'Set language & market', 'Localize with Seedance'],
    sourceTag: 'localize_ad_template',
    directCompose: true,
    requiresLocalizationTarget: true,
    buildPrompt: (variant = {}, context = {}) => {
      const language = (context.targetLanguage || '').trim();
      const market = (context.targetMarket || '').trim();
      const notes = (context.localizationNotes || '').trim();

      return `Use the localize-video skill to localize the attached ad.

Target language: ${language}
Target market: ${market}
Notes: ${notes || 'Use natural, market-appropriate language.'}`;
    },
  },
};

function clampVariantCount(value) {
  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed)) return MIN_VARIANTS;
  return Math.max(MIN_VARIANTS, Math.min(MAX_VARIANTS, parsed));
}

function isSupportedVideoFile(file) {
  const filename = (file?.name || '').toLowerCase();
  return (
    file?.type?.startsWith('video/') ||
    filename.endsWith('.mp4') ||
    filename.endsWith('.mov') ||
    filename.endsWith('.webm')
  );
}

function isSupportedImageFile(file) {
  const filename = (file?.name || '').toLowerCase();
  return (
    file?.type?.startsWith('image/') ||
    filename.endsWith('.jpg') ||
    filename.endsWith('.jpeg') ||
    filename.endsWith('.png') ||
    filename.endsWith('.webp') ||
    filename.endsWith('.gif')
  );
}

function VideoVariantTemplatePage({ templateId }) {
  const config = TEMPLATE_CONFIGS[templateId] || TEMPLATE_CONFIGS['change-style'];
  const Icon = config.icon;
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const styleReferenceInputRef = useRef(null);
  const styleReferenceFilesRef = useRef([]);
  const pendingSubmitRef = useRef(null);
  const {
    user, loading, openAuthModal, closeAuthModal, authModalOpen,
    signInWithGoogle, signInWithEmail,
  } = useAuthContext();

  const [pagePhase, setPagePhase] = useState('input');
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState('');
  const [styleInstruction, setStyleInstruction] = useState('');
  const [styleReferenceFiles, setStyleReferenceFiles] = useState([]);
  const [targetLanguage, setTargetLanguage] = useState('');
  const [targetMarket, setTargetMarket] = useState('');
  const [localizationNotes, setLocalizationNotes] = useState('');
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [characterTab, setCharacterTab] = useState('my');
  const [characterSearch, setCharacterSearch] = useState('');
  const [sourceAspectRatio, setSourceAspectRatio] = useState('9:16');
  const [numVariants, setNumVariants] = useState(3);
  const [variants, setVariants] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const [generatingProjects, setGeneratingProjects] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [showAutoReloadModal, setShowAutoReloadModal] = useState(false);
  const {
    items: myCharacters,
    isLoading: myCharactersLoading,
    error: myCharactersError,
    hasCache: myCharactersHasCache,
  } = useCharactersData(user?.id);
  const {
    items: libraryCharacters,
    isLoading: libraryCharactersLoading,
    error: libraryCharactersError,
    hasCache: libraryCharactersHasCache,
  } = useLibraryCharactersData(user?.id);

  useEffect(() => {
    return () => {
      if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    };
  }, [videoPreviewUrl]);

  useEffect(() => {
    styleReferenceFilesRef.current = styleReferenceFiles;
  }, [styleReferenceFiles]);

  useEffect(() => () => {
    styleReferenceFilesRef.current.forEach((reference) => {
      if (reference.previewUrl) URL.revokeObjectURL(reference.previewUrl);
    });
  }, []);

  const isMultiSelect = eligibility?.auto_reload_enabled === true;
  const selectedCount = variants.filter((variant) => variant.selected).length;
  const isDirectCompose = config.directCompose === true;
  const requiresStyleTarget = config.requiresStyleTarget === true;
  const requiresCharacterTarget = config.requiresCharacterTarget === true;
  const requiresLocalizationTarget = config.requiresLocalizationTarget === true;
  const hasStyleTarget = styleInstruction.trim().length > 0 || styleReferenceFiles.length > 0;
  const hasCharacterTarget = Boolean(selectedCharacter);
  const hasLocalizationTarget = targetLanguage.trim().length > 0 && targetMarket.trim().length > 0;
  const canSubmitInput = Boolean(videoFile)
    && (!requiresStyleTarget || hasStyleTarget)
    && (!requiresCharacterTarget || hasCharacterTarget)
    && (!requiresLocalizationTarget || hasLocalizationTarget)
    && pagePhase === 'input';
  const characterSourceList = characterTab === 'my' ? myCharacters : libraryCharacters;
  const characterLoading = characterTab === 'my'
    ? myCharactersLoading && !myCharactersHasCache
    : libraryCharactersLoading && !libraryCharactersHasCache;
  const characterHasCache = characterTab === 'my' ? myCharactersHasCache : libraryCharactersHasCache;
  const characterError = characterTab === 'my' ? myCharactersError : libraryCharactersError;

  const filteredCharacters = useMemo(() => {
    const query = characterSearch.trim().toLowerCase();
    if (!query) return characterSourceList;
    return characterSourceList.filter((character) => {
      if (character.name?.toLowerCase().includes(query)) return true;
      if (character.personality?.toLowerCase().includes(query)) return true;
      if (character.physicalAppearance?.toLowerCase().includes(query)) return true;
      if (character.keywords?.some((keyword) => keyword.toLowerCase().includes(query))) return true;
      return false;
    });
  }, [characterSearch, characterSourceList]);

  useEffect(() => {
    if (!requiresCharacterTarget || loading || !user) return;
    fetchCharactersIfStale({ userId: user.id });
    fetchLibraryCharactersIfStale({ userId: user.id });
  }, [loading, requiresCharacterTarget, user]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleStyleReferenceUploadClick = () => {
    styleReferenceInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!isSupportedVideoFile(file)) {
      setSubmitError('Please upload a video file.');
      event.target.value = '';
      return;
    }

    setVideoFile(file);
    setVideoPreviewUrl(URL.createObjectURL(file));
    setUploadedVideoUrl('');
    setVariants([]);
    setGeneratingProjects({});
    setSubmitError(null);
    setPagePhase('input');
    event.target.value = '';
  };

  const handleStyleReferenceChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const invalidFile = files.find((file) => !isSupportedImageFile(file));
    if (invalidFile) {
      setSubmitError('Please upload JPG, PNG, WebP, or GIF style reference images.');
      event.target.value = '';
      return;
    }

    setStyleReferenceFiles((prev) => {
      const remainingSlots = MAX_STYLE_REFERENCE_IMAGES - prev.length;
      const acceptedFiles = files.slice(0, Math.max(remainingSlots, 0));
      const nextReferences = acceptedFiles.map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
        file,
        previewUrl: URL.createObjectURL(file),
      }));
      return [...prev, ...nextReferences];
    });
    setVariants([]);
    setGeneratingProjects({});
    setSubmitError(
      styleReferenceFiles.length + files.length > MAX_STYLE_REFERENCE_IMAGES
        ? `Using the first ${MAX_STYLE_REFERENCE_IMAGES} style reference images.`
        : null
    );
    setPagePhase('input');
    event.target.value = '';
  };

  const handleRemoveStyleReference = (referenceId) => {
    setStyleReferenceFiles((prev) => {
      const removed = prev.find((reference) => reference.id === referenceId);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((reference) => reference.id !== referenceId);
    });
    setVariants([]);
    setGeneratingProjects({});
    setSubmitError(null);
    setPagePhase('input');
  };

  const handleStyleInstructionChange = (event) => {
    setStyleInstruction(event.target.value);
    setVariants([]);
    setGeneratingProjects({});
    setSubmitError(null);
    setPagePhase('input');
  };

  const handleSelectCharacter = (character) => {
    setSelectedCharacter(character);
    setVariants([]);
    setGeneratingProjects({});
    setSubmitError(null);
    setPagePhase('input');
  };

  const handleCharacterTabChange = (tab) => {
    setCharacterTab(tab);
    setCharacterSearch('');
  };

  const handleRemoveVideo = () => {
    setVideoFile(null);
    setVideoPreviewUrl(null);
    setUploadedVideoUrl('');
    setVariants([]);
    setGeneratingProjects({});
    setSubmitError(null);
    setPagePhase('input');
  };

  const handleLoadedMetadata = (event) => {
    const { videoWidth, videoHeight } = event.currentTarget;
    if (!videoWidth || !videoHeight) return;
    setSourceAspectRatio(videoWidth > videoHeight ? '16:9' : '9:16');
  };

  const refreshEligibility = useCallback(async () => {
    if (!user) return null;
    try {
      const nextEligibility = await getAgentEligibility();
      setEligibility(nextEligibility);
      return nextEligibility;
    } catch (err) {
      console.error('Eligibility check failed:', err);
      return null;
    }
  }, [user]);

  const handleGenerateVariants = useCallback(async () => {
    if (!videoFile) return;
    setPagePhase('generating_variants');
    setSubmitError(null);

    try {
      const result = await generateTemplateVariants({
        templateId,
        videoFile,
        numVariants,
      });
      const nextEligibility = await refreshEligibility();
      const canMulti = nextEligibility?.auto_reload_enabled === true;
      setUploadedVideoUrl(result.videoUrl);
      setVariants((result.variants || []).map((variant, index) => ({
        ...variant,
        selected: canMulti || index === 0,
        expanded: index === 0,
      })));
      setPagePhase('showing_variants');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error(`Failed to generate ${config.variantPlural}:`, err);
      setSubmitError(`Failed to generate ${config.variantPlural}. Please try again.`);
      setPagePhase('input');
    }
  }, [
    config.variantPlural,
    numVariants,
    refreshEligibility,
    templateId,
    videoFile,
  ]);

  const startDirectProjectAndNavigate = useCallback(async () => {
    if (!videoFile) {
      throw new Error('Please upload a source video.');
    }

    const projectId = Math.random().toString(36).substring(2, 15);
    const prompt = config.buildPrompt({}, {
      styleInstruction: requiresStyleTarget ? styleInstruction : '',
      styleReferenceCount: requiresStyleTarget ? styleReferenceFiles.length : 0,
      selectedCharacter: requiresCharacterTarget ? selectedCharacter : null,
      targetLanguage: requiresLocalizationTarget ? targetLanguage : '',
      targetMarket: requiresLocalizationTarget ? targetMarket : '',
      localizationNotes: requiresLocalizationTarget ? localizationNotes : '',
    });
    const files = [
      videoFile,
      ...styleReferenceFiles.map((reference) => reference.file),
    ];
    await initializeProject(projectId, prompt, sourceAspectRatio);
    await startAgentCompose(
      projectId,
      prompt,
      files,
      'pro',
      [],
      config.sourceTag,
      {
        analyzeVideoAttachments: true,
        ...(config.videoModel ? {
          defaultVideoModel: config.videoModel,
          availableVideoModels: [config.videoModel],
        } : {}),
      }
    );

    navigate(`/project/${projectId}`, {
      state: { agentMode: true, promptText: prompt },
    });
  }, [
    config,
    localizationNotes,
    navigate,
    requiresCharacterTarget,
    requiresLocalizationTarget,
    requiresStyleTarget,
    selectedCharacter,
    sourceAspectRatio,
    styleInstruction,
    styleReferenceFiles,
    targetLanguage,
    targetMarket,
    videoFile,
  ]);

  const handleDirectCompose = useCallback(async () => {
    if (
      !videoFile
      || (requiresStyleTarget && !hasStyleTarget)
      || (requiresCharacterTarget && !hasCharacterTarget)
      || (requiresLocalizationTarget && !hasLocalizationTarget)
    ) return;

    if (!user && !loading) {
      pendingSubmitRef.current = 'direct_compose';
      openAuthModal();
      return;
    }

    const nextEligibility = await refreshEligibility();
    if (nextEligibility && !nextEligibility.allowed && nextEligibility.reason === 'auto_reload_required') {
      setShowAutoReloadModal(true);
      return;
    }

    setPagePhase('generating_direct');
    setSubmitError(null);
    try {
      await startDirectProjectAndNavigate();
    } catch (err) {
      console.error(`${config.title} direct generation failed:`, err);
      setSubmitError('Failed to start generation. Please try again.');
      setPagePhase('input');
    }
  }, [
    config.title,
    hasLocalizationTarget,
    hasStyleTarget,
    hasCharacterTarget,
    loading,
    openAuthModal,
    refreshEligibility,
    requiresCharacterTarget,
    requiresLocalizationTarget,
    requiresStyleTarget,
    startDirectProjectAndNavigate,
    user,
    videoFile,
  ]);

  const handleToggleVariant = (idx) => {
    setVariants((prev) => prev.map((variant, index) => {
      if (index === idx) return { ...variant, selected: !variant.selected };
      if (!isMultiSelect) return { ...variant, selected: false };
      return variant;
    }));
  };

  const handleToggleExpand = (idx) => {
    setVariants((prev) => prev.map((variant, index) => (
      index === idx ? { ...variant, expanded: !variant.expanded } : variant
    )));
  };

  const handleUpdateInstruction = (idx, value) => {
    setVariants((prev) => prev.map((variant, index) => (
      index === idx ? { ...variant, variantInstruction: value } : variant
    )));
  };

  const generateSingleProjectAndNavigate = useCallback(async (variant) => {
    if (!uploadedVideoUrl) {
      throw new Error('Source video is still uploading. Please generate variants again.');
    }

    const projectId = Math.random().toString(36).substring(2, 15);
    const prompt = config.buildPrompt(variant);

    await initializeProject(projectId, prompt, sourceAspectRatio);
    await startAgentCompose(
      projectId,
      prompt,
      [],
      'pro',
      [uploadedVideoUrl],
      config.sourceTag,
      { analyzeVideoAttachments: true }
    );

    navigate(`/project/${projectId}`, {
      state: { agentMode: true, promptText: prompt },
    });
  }, [
    config,
    navigate,
    sourceAspectRatio,
    uploadedVideoUrl,
  ]);

  const generateSingleProject = useCallback(async (variant, idx) => {
    const projectId = Math.random().toString(36).substring(2, 15);
    const prompt = config.buildPrompt(variant);
    setGeneratingProjects((prev) => ({ ...prev, [idx]: { status: 'initializing', projectId } }));

    try {
      await initializeProject(projectId, prompt, sourceAspectRatio);
      setGeneratingProjects((prev) => ({ ...prev, [idx]: { status: 'composing', projectId } }));
      await startAgentCompose(
        projectId,
        prompt,
        [],
        'pro',
        [uploadedVideoUrl],
        config.sourceTag,
        { analyzeVideoAttachments: true }
      );
      setGeneratingProjects((prev) => ({ ...prev, [idx]: { status: 'done', projectId } }));
    } catch (err) {
      console.error(`${config.title} project generation failed for variant ${idx}:`, err);
      setGeneratingProjects((prev) => ({
        ...prev,
        [idx]: { status: 'error', projectId, error: 'Failed' },
      }));
    }
  }, [
    config,
    sourceAspectRatio,
    uploadedVideoUrl,
  ]);

  const handleGenerateVideos = useCallback(async () => {
    const selectedVariants = variants
      .map((variant, idx) => ({ ...variant, idx }))
      .filter((variant) => variant.selected && variant.variantInstruction.trim());

    if (selectedVariants.length === 0) return;

    if (!user && !loading) {
      pendingSubmitRef.current = 'generate_videos';
      openAuthModal();
      return;
    }

    const nextEligibility = await refreshEligibility();
    if (nextEligibility && !nextEligibility.allowed && nextEligibility.reason === 'auto_reload_required') {
      setShowAutoReloadModal(true);
      return;
    }

    setSubmitError(null);
    setPagePhase('generating_videos');
    const canMulti = nextEligibility?.auto_reload_enabled === true;

    if (canMulti) {
      await Promise.allSettled(
        selectedVariants.map((variant) => generateSingleProject(variant, variant.idx))
      );
      return;
    }

    try {
      await generateSingleProjectAndNavigate(selectedVariants[0]);
    } catch (err) {
      console.error(`${config.title} generation failed:`, err);
      setSubmitError('Failed to start generation. Please try again.');
      setPagePhase('showing_variants');
    }
  }, [
    config.title,
    generateSingleProject,
    generateSingleProjectAndNavigate,
    loading,
    openAuthModal,
    refreshEligibility,
    user,
    variants,
  ]);

  const handleBackToInput = () => {
    setVariants([]);
    setGeneratingProjects({});
    setSubmitError(null);
    setPagePhase('input');
  };

  const handleEnableAutoReloadAndContinue = useCallback(async () => {
    setShowAutoReloadModal(false);
    if (isDirectCompose) {
      await handleDirectCompose();
      return;
    }
    await handleGenerateVideos();
  }, [handleDirectCompose, handleGenerateVideos, isDirectCompose]);

  useEffect(() => {
    if (!user || !pendingSubmitRef.current) return;
    const action = pendingSubmitRef.current;
    pendingSubmitRef.current = null;
    if (action === 'generate_videos') {
      handleGenerateVideos();
    } else if (action === 'direct_compose') {
      handleDirectCompose();
    }
  }, [handleDirectCompose, handleGenerateVideos, user]);

  return (
    <UniversalLayout>
      <div className="hook-content">
        <main className="hook-main">
          <header className="hook-header">
            <span className="hook-eyebrow">Template</span>
            <h1 className="hook-title">
              {config.titlePrefix} <span className="hook-title-em">{config.titleEm}</span>
            </h1>
            <p className="hook-subtitle">{config.subtitle}</p>
          </header>

          {(pagePhase === 'input' || pagePhase === 'generating_variants' || pagePhase === 'generating_direct') && (
            <div className="hook-config-grid">
              <section className="hook-panel hook-panel--form">
                <div className="hook-section-header">
                  <span className="hook-section-number">01</span>
                  <div>
                    <h2>Source video</h2>
                    <p>{config.sourceDescription}</p>
                  </div>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm"
                  className="hook-file-input"
                  onChange={handleFileChange}
                />

                {videoPreviewUrl ? (
                  <div className="hook-video-preview">
                    <video
                      src={videoPreviewUrl}
                      controls
                      onLoadedMetadata={handleLoadedMetadata}
                    />
                    <div className="hook-video-meta">
                      <FileVideo size={16} strokeWidth={1.8} aria-hidden="true" />
                      <span>{videoFile?.name}</span>
                      <button type="button" onClick={handleRemoveVideo} aria-label="Remove video">
                        <X size={15} strokeWidth={1.9} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button type="button" className="hook-upload-zone" onClick={handleUploadClick}>
                    <span className="hook-upload-icon" aria-hidden="true">
                      <Upload size={24} strokeWidth={1.7} />
                    </span>
                    <span className="hook-upload-title">Upload a video</span>
                    <span className="hook-upload-hint">MP4, MOV, or WebM up to 500MB</span>
                  </button>
                )}

                {requiresStyleTarget && (
                  <div className="hook-style-target">
                    <div className="hook-section-header hook-section-header--compact">
                      <span className="hook-section-number">02</span>
                      <div>
                        <h2>{config.styleTargetLabel}</h2>
                        <p>{config.styleTargetDescription}</p>
                      </div>
                    </div>

                    <textarea
                      className="hook-style-textarea"
                      value={styleInstruction}
                      onChange={handleStyleInstructionChange}
                      placeholder={config.styleTargetPlaceholder}
                      rows={4}
                    />

                    <input
                      ref={styleReferenceInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hook-file-input"
                      multiple
                      onChange={handleStyleReferenceChange}
                    />

                    <div className="hook-style-reference-bar">
                      <button
                        type="button"
                        className="hook-style-upload-btn"
                        onClick={handleStyleReferenceUploadClick}
                        disabled={styleReferenceFiles.length >= MAX_STYLE_REFERENCE_IMAGES}
                      >
                        <ImagePlus size={16} strokeWidth={1.9} />
                        Add reference images
                      </button>
                      <span>{styleReferenceFiles.length}/{MAX_STYLE_REFERENCE_IMAGES}</span>
                    </div>

                    {styleReferenceFiles.length > 0 && (
                      <div className="hook-style-reference-grid">
                        {styleReferenceFiles.map((reference) => (
                          <div className="hook-style-reference-thumb" key={reference.id}>
                            <img src={reference.previewUrl} alt="" />
                            <button
                              type="button"
                              onClick={() => handleRemoveStyleReference(reference.id)}
                              aria-label="Remove style reference"
                            >
                              <X size={13} strokeWidth={2} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {requiresLocalizationTarget && (
                  <div className="hook-localization-target">
                    <div className="hook-section-header hook-section-header--compact">
                      <span className="hook-section-number">02</span>
                      <div>
                        <h2>Target locale</h2>
                        <p>Choose one language and market for this localized ad.</p>
                      </div>
                    </div>

                    <div className="hook-localization-grid">
                      <label>
                        <span>Target language</span>
                        <input
                          type="text"
                          value={targetLanguage}
                          onChange={(event) => setTargetLanguage(event.target.value)}
                          placeholder="e.g. Spanish"
                          autoComplete="off"
                        />
                      </label>
                      <label>
                        <span>Target market</span>
                        <input
                          type="text"
                          value={targetMarket}
                          onChange={(event) => setTargetMarket(event.target.value)}
                          placeholder="e.g. Mexico"
                          autoComplete="off"
                        />
                      </label>
                    </div>

                    <label className="hook-localization-notes">
                      <span>Localization notes <small>Optional</small></span>
                      <textarea
                        className="hook-style-textarea"
                        value={localizationNotes}
                        onChange={(event) => setLocalizationNotes(event.target.value)}
                        placeholder="Official translated product names, required CTA wording, currency or unit changes, legal text, tone, dialect, and cultural details."
                        rows={4}
                      />
                    </label>

                    <p className="hook-localization-model-note">
                      Experimental: Seedance 2.5 will attempt to translate the original spoken audio directly. If the result is inaccurate or unsynchronized, the workflow falls back to a separate localized voiceover.
                    </p>
                  </div>
                )}

                {requiresCharacterTarget && (
                  <div className="hook-character-target">
                    <div className="hook-section-header hook-section-header--compact">
                      <span className="hook-section-number">02</span>
                      <div>
                        <h2>{config.characterTargetLabel}</h2>
                        <p>{config.characterTargetDescription}</p>
                      </div>
                    </div>

                    {!user && !loading ? (
                      <div className="hook-character-auth">
                        <p>Sign in to choose from your characters and the Latted library.</p>
                        <button type="button" className="hook-btn-secondary" onClick={openAuthModal}>
                          Sign in
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="hook-character-tabs">
                          <button
                            type="button"
                            className={characterTab === 'my' ? 'hook-character-tab hook-character-tab--active' : 'hook-character-tab'}
                            onClick={() => handleCharacterTabChange('my')}
                          >
                            My Characters
                          </button>
                          <button
                            type="button"
                            className={characterTab === 'library' ? 'hook-character-tab hook-character-tab--active' : 'hook-character-tab'}
                            onClick={() => handleCharacterTabChange('library')}
                          >
                            Character Library
                          </button>
                        </div>

                        <input
                          type="text"
                          className="hook-character-search"
                          value={characterSearch}
                          onChange={(event) => setCharacterSearch(event.target.value)}
                          placeholder="Search characters..."
                        />

                        {characterError && <span className="hook-error">{characterError}</span>}
                        {characterLoading && <div className="hook-character-state">Loading characters...</div>}
                        {characterHasCache && !characterLoading && !characterError && filteredCharacters.length === 0 && (
                          <div className="hook-character-state">
                            {characterSearch.trim() ? 'No characters match your search.' : 'No characters available.'}
                          </div>
                        )}

                        {filteredCharacters.length > 0 && (
                          <div className="hook-character-grid">
                            {filteredCharacters.map((character) => {
                              const isSelected = selectedCharacter?.id === character.id;
                              const description = character.personality || character.physicalAppearance || character.keywords?.join(', ') || '';
                              return (
                                <button
                                  type="button"
                                  key={character.id}
                                  className={isSelected ? 'hook-character-card hook-character-card--selected' : 'hook-character-card'}
                                  onClick={() => handleSelectCharacter(character)}
                                >
                                  <span className="hook-character-thumb" aria-hidden="true">
                                    <span>{character.name?.charAt(0)?.toUpperCase() || '?'}</span>
                                  </span>
                                  <span className="hook-character-copy">
                                    <strong>{character.name || 'Unnamed character'}</strong>
                                    {description && <small>{description}</small>}
                                  </span>
                                  {isSelected && (
                                    <span className="hook-character-check" aria-hidden="true">
                                      <Check size={12} strokeWidth={3} />
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {!isDirectCompose && (
                  <div className="hook-control-row">
                    <label htmlFor={`${templateId}-count`}>{config.countLabel}</label>
                    <input
                      id={`${templateId}-count`}
                      type="number"
                      min={MIN_VARIANTS}
                      max={MAX_VARIANTS}
                      value={numVariants}
                      onChange={(event) => setNumVariants(clampVariantCount(event.target.value))}
                    />
                  </div>
                )}

                <button
                  type="button"
                  className="hook-btn-submit"
                  disabled={!canSubmitInput || pagePhase === 'generating_variants' || pagePhase === 'generating_direct'}
                  onClick={isDirectCompose ? handleDirectCompose : handleGenerateVariants}
                >
                  {pagePhase === 'generating_variants' || pagePhase === 'generating_direct' ? (
                    <>
                      <span className="hook-spinner" />
                      {config.generatingLabel}
                    </>
                  ) : (
                    <>
                      <Wand2 size={17} strokeWidth={1.9} />
                      {config.generateLabel}
                    </>
                  )}
                </button>

                {submitError && <span className="hook-error">{submitError}</span>}
              </section>

              <aside className="hook-panel hook-panel--aside">
                <div className="hook-aside-icon" aria-hidden="true">
                  <Icon size={22} strokeWidth={1.7} />
                </div>
                <h2>What this does</h2>
                <p>{config.whatThisDoes}</p>
                <div className="hook-aside-steps">
                  {config.steps.map((step) => (
                    <span key={step}>{step}</span>
                  ))}
                </div>
              </aside>
            </div>
          )}

          {(pagePhase === 'showing_variants' || pagePhase === 'generating_videos') && (
            <section className="hook-results">
              <div className="hook-results-header">
                <div>
                  <h2>
                    {config.resultsTitle}
                    <span>{variants.length}</span>
                  </h2>
                  <p>
                    {isMultiSelect
                      ? `Select any ${config.variantPlural} to rebuild in parallel.`
                      : `Select one ${config.variantNoun} to rebuild. Auto-reload credits enable batch generation.`}
                  </p>
                </div>
                {pagePhase === 'showing_variants' && (
                  <button type="button" className="hook-btn-secondary" onClick={handleBackToInput}>
                    <RotateCcw size={15} strokeWidth={1.9} />
                    Change source
                  </button>
                )}
              </div>

              <div className="hook-list">
                {variants.map((variant, idx) => {
                  const project = generatingProjects[idx];
                  return (
                    <article
                      key={`${variant.title}-${idx}`}
                      className={`hook-card${variant.selected ? ' hook-card--selected' : ''}${project ? ' hook-card--generating' : ''}`}
                    >
                      <div className="hook-card-header">
                        {pagePhase === 'showing_variants' && (
                          <button
                            type="button"
                            className={`hook-select${variant.selected ? ' hook-select--active' : ''}`}
                            onClick={() => handleToggleVariant(idx)}
                            aria-label={variant.selected ? 'Deselect variant' : 'Select variant'}
                          >
                            {variant.selected && <Check size={13} strokeWidth={3} />}
                          </button>
                        )}
                        <div className="hook-card-title-wrap">
                          <h3>{variant.title}</h3>
                          {variant.strategy && <p>{variant.strategy}</p>}
                        </div>
                        {project && (
                          <div className="hook-project-status">
                            {(project.status === 'initializing' || project.status === 'composing') && (
                              <span className="hook-project-spinner" />
                            )}
                            {project.status === 'done' && (
                              <button
                                type="button"
                                onClick={() => navigate(`/project/${project.projectId}`)}
                              >
                                Go to Project
                              </button>
                            )}
                            {project.status === 'error' && <span>Failed</span>}
                          </div>
                        )}
                      </div>

                      {variant.expanded ? (
                        <textarea
                          className="hook-script-input"
                          value={variant.variantInstruction}
                          onChange={(event) => handleUpdateInstruction(idx, event.target.value)}
                          rows={5}
                          disabled={pagePhase === 'generating_videos'}
                        />
                      ) : (
                        <p className="hook-script-preview">
                          {variant.variantInstruction.slice(0, 220)}
                          {variant.variantInstruction.length > 220 ? '...' : ''}
                        </p>
                      )}

                      <button
                        type="button"
                        className="hook-expand-btn"
                        onClick={() => handleToggleExpand(idx)}
                      >
                        {variant.expanded ? 'Collapse instruction' : 'Edit full instruction'}
                      </button>
                    </article>
                  );
                })}
              </div>

              {pagePhase === 'showing_variants' && (
                <div className="hook-submit-area">
                  <button
                    type="button"
                    className="hook-btn-submit"
                    disabled={selectedCount === 0}
                    onClick={handleGenerateVideos}
                  >
                    {selectedCount === 0
                      ? `Select ${config.variantPlural} to generate`
                      : selectedCount === 1
                        ? 'Generate 1 Video'
                        : `Generate ${selectedCount} Videos`}
                  </button>
                  {submitError && <span className="hook-error">{submitError}</span>}
                </div>
              )}

              {pagePhase === 'generating_videos' && (
                <div className="hook-submit-area">
                  <p className="hook-generating-label">
                    <span className="hook-project-spinner" />
                    Starting video generations
                  </p>
                </div>
              )}
            </section>
          )}
        </main>
      </div>

      <AuthModal
        isOpen={!loading && !user && authModalOpen}
        onClose={closeAuthModal}
        onSignIn={() => signInWithGoogle()}
        onEmailSignIn={signInWithEmail}
      />

      <AgentAutoReloadModal
        isOpen={showAutoReloadModal}
        onClose={() => setShowAutoReloadModal(false)}
        onEnableAutoReload={handleEnableAutoReloadAndContinue}
        onCreateBlankProject={() => {
          setShowAutoReloadModal(false);
          if (isDirectCompose) {
            setPagePhase('generating_direct');
            startDirectProjectAndNavigate().catch((err) => {
              console.error(`${config.title} fallback generation failed:`, err);
              setSubmitError('Failed to start generation.');
              setPagePhase('input');
            });
            return;
          }
          const firstSelected = variants.find((variant) => variant.selected);
          if (firstSelected) {
            generateSingleProjectAndNavigate(firstSelected).catch((err) => {
              console.error(`${config.title} fallback generation failed:`, err);
              setSubmitError('Failed to start generation.');
              setPagePhase('showing_variants');
            });
          }
        }}
      />
    </UniversalLayout>
  );
}

export default VideoVariantTemplatePage;
